/* eslint-disable camelcase */
import * as Yup from 'yup';

import { http, authHttp } from 'lib/services/http';
import { platformAdminBaseUrl } from 'constants/apiUrls';
import { yupErrorHandler } from 'api/apiUtils';
import { IGetUserInfo } from 'containers/pages/Messaging/CampaignDialog/WizardContextV3/useGetUserInfo';

const USERS_API_PATH = `${platformAdminBaseUrl}/users`;
const USERS_API_PATH_V2 = `${platformAdminBaseUrl}/usersv2`;
const USER_LICENSES_PATH = `${platformAdminBaseUrl}/licenses`;

type Role = {
  description: string;
  id: number;
  name: string;
};

type User = {
  active: boolean;
  createdDate: string;
  email: string;
  firstName: string;
  lastName: string;
  nextivaUuid: string;
  roles: Role[];
  status: string;
  timeZone: string;
  updatedDate: string;
};

type Response = { result: User[]; resultCount: number };

export const getUserList = (authV3Enabled: boolean) => {
  return authV3Enabled
    ? authHttp.get<any, Response>(`/users`)
    : http.get<any, Response>(`${USERS_API_PATH}/list`);
};

export const userDeactivateBulk = (userList, authV3Enabled) => {
  return authV3Enabled
    ? authHttp.put(`/users/status/deactivate`, userList)
    : http.post(`${USERS_API_PATH}/deactivate/bulk`, userList);
};

export const getInfoService = () => {
  return http.get('/platform-admin/v1/getme');
};

export const userReactivateBulk = (userList, authV3Enabled) => {
  if (authV3Enabled) return authHttp.put('/users/status/reactivate', userList);
  return http.post(`${USERS_API_PATH}/reactivate/bulk`, userList);
};

export const passwordResetBulk = (loginIds, authV3Enabled) => {
  return authV3Enabled
    ? authHttp.post('/users/password-reset', {
        bulkUsers: loginIds.map((nextivaUuid) => ({ nextivaUuid })),
      })
    : http.post(`${USERS_API_PATH}/platform/passwordreset/bulk`, loginIds);
};

const model = Yup.object().shape({
  add_ids: Yup.array().of(Yup.number().positive().integer().required()),
  email: Yup.string().required(),
  first_name: Yup.string().required(),
  last_name: Yup.string().required(),
  locationId: Yup.number().positive().integer(),
  phone: Yup.string(),
  preferred_time_zone: Yup.string(),
});

const usersModel = Yup.object().shape({
  email: Yup.string().required(),
  firstName: Yup.string().required(),
  lastName: Yup.string().required(),
  locationId: Yup.number().positive().integer(),
  phone: Yup.string(),
  preferredTimeZone: Yup.string(),
  roleTeamIds: Yup.array().of(Yup.number().positive().integer().required()),
});

export const postUsers = (userObj, authV3Enabled) => {
  if (authV3Enabled) {
    return usersModel
      .validate(userObj)
      .then(() => {
        return authHttp.post('/users', userObj);
      })
      .catch((err) => {
        throw yupErrorHandler(err.errors);
      });
  }

  return model
    .validate(userObj)
    .then(() => {
      return http.post(`${USERS_API_PATH_V2}`, userObj);
    })
    .catch((err) => {
      throw yupErrorHandler(err.errors);
    });
};

export const postResetPassword = (loginId, authV3Enabled) => {
  return authV3Enabled
    ? authHttp.post('/users/password-reset', {
        bulkUsers: [{ nextivaUuid: loginId }],
      })
    : http.post(
        `${USERS_API_PATH}/platform/passwordreset?login_id=${encodeURIComponent(
          loginId,
        )}`,
      );
};

const updateUserModal = Yup.object().shape({
  add_ids: Yup.array().of(Yup.number().positive().integer()),
  drop_ids: Yup.array().of(Yup.number().positive().integer()),
  first_name: Yup.string().max(30),
  is_super_admin: Yup.boolean(),
  last_name: Yup.string().max(40),
  locationId: Yup.number().positive().integer(),
  phone: Yup.string(),
  preferred_time_zone: Yup.string(),
});

const updateUserModalV3 = Yup.object().shape({
  active: Yup.boolean(),
  createdDate: Yup.string(),
  deactivatedDate: Yup.string(),
  email: Yup.string(),
  extension: Yup.string(),
  nextivaUuid: Yup.string(),
  roleDisplay: Yup.string(),
  security_pin: Yup.string(),
  locationDisplay: Yup.string(),
  status: Yup.string(),
  firstName: Yup.string().max(30),
  lastName: Yup.string().max(40),
  timeZone: Yup.string(),
  phoneNumber: Yup.string(),
  updatedDate: Yup.string(),
});

export const updateUser = (email, body, userId, authV3Enabled) => {
  if (authV3Enabled) {
    return updateUserModalV3
      .validate(body)
      .then(() => {
        return authHttp.put(`/users/${userId}`, body);
      })
      .catch((err) => {
        throw yupErrorHandler(err.errors);
      });
  }
  return updateUserModal
    .validate(body)
    .then(() => {
      return http.put(`${USERS_API_PATH_V2}/${email}`, body);
    })
    .catch((err) => {
      throw yupErrorHandler(err.errors);
    });
};

export const getUser = (
  email: string,
  authV3Enabled: boolean,
  nextivaUUID: string,
): Promise<IGetUserInfo> => {
  return authV3Enabled
    ? authHttp.get(`/users/${nextivaUUID}`)
    : http.get(`${USERS_API_PATH}/${email}/retrieve`);
};

const userLicenses = Yup.number().positive().integer().required();

// TODO: replace with LicenseService Api and move to licensesService.js
export const getUserLicenses = (
  profileId,
  authV3Enabled,
  corpAcctNbr,
  nextivaUUID,
) => {
  return userLicenses
    .validate(profileId)
    .then(() => {
      return authV3Enabled
        ? authHttp.get(
            `/corp-account/${corpAcctNbr}/users/${nextivaUUID}/licenses`,
          )
        : http.get(`${USER_LICENSES_PATH}/profile/${profileId}`);
    })
    .catch((err) => {
      throw yupErrorHandler(err.errors);
    });
};
